import React, { useEffect, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, Platform } from 'react-native';
import { FontAwesome } from '@expo/vector-icons'; // Assurez-vous d'installer @expo/vector-icons
import Legend from './Legend';
import Queries from '../../api/queries';
import { ActivityIndicator } from 'react-native-paper';
import Victory from '../utils/Victory';

type PieData = {
    key: number;
    amount: number;
    color: string;
    label: string;
};

const getRandomColor = () => {
    let color;
    do {
        color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    } while (color.toUpperCase() === '#FFFFFF' || color.length < 7); // Evite le blanc et les couleurs courtes
    return color;
};

const Details = ({ date }) => {
    const queries = new Queries();
    const [isExpensesExpanded, setIsExpensesExpanded] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);


    const fetchInvoiceDetails = (date) => {
        queries.getInvoiceDetail(date)
            .then(response => {
                setInvoiceDetails(response?.data?.OA_getInvoiceDetail);
                setLoading(false);
            })
            .catch(error => {
                setError(error);
                setLoading(false);
            });
    };

    useEffect(() => {
        if (date) {
            fetchInvoiceDetails(date);
        }
    }, [date]);

    if (loading) return <ActivityIndicator />;
    if (error) return <Text>Error: {error.message}</Text>;

    const revenues = invoiceDetails?.revenues || invoiceDetails?.revenuesDetails?.reduce((total, item) => total + item.amount, 0);
    const expenses = invoiceDetails?.expenses || invoiceDetails?.expensesDetails?.reduce((total, item) => total + item.amount, 0);
    const netIncome = revenues - expenses;

    const chartData: PieData[] = [
        {
            key: 1,
            amount: revenues,
            color: '#C4027A',
            label: 'Recettes',
        },
        {
            key: 2,
            amount: expenses,
            color: '#A9A9A9',
            label: 'Dépenses',
        },
    ];

    const chartData2: PieData[] = invoiceDetails?.expensesDetails?.map((expense, index) => ({
        key: index,
        amount: expense.amount,
        color: getRandomColor(),
        label: expense.label,
    }));

    return (
        <View style={styles.container}>
            <Text style={styles.heading}>En détail</Text>
            <View style={styles.tabContent}>
                <Text style={styles.label}>Recettes</Text>
                <Text style={styles.value}>{revenues?.toFixed(2)} €</Text>
            </View>
            <TouchableOpacity disabled={!invoiceDetails?.expensesDetails} onPress={() => setIsExpensesExpanded(!isExpensesExpanded)}>
                <View style={styles.tabContent}>
                    <View style={styles.tabContent}>
                        {invoiceDetails?.expensesDetails && (
                            <FontAwesome
                                name={isExpensesExpanded ? 'chevron-down' : 'chevron-right'}
                                size={18}
                                color="#C4027A"
                                style={styles.chevron}
                            />)
                        }
                        <Text style={styles.label}>Dépenses</Text>
                    </View>
                    <Text style={styles.value}>{expenses?.toFixed(2)} €</Text>
                </View>
            </TouchableOpacity>
            {isExpensesExpanded && (
                <View>
                    {invoiceDetails?.expensesDetails?.map((item, index) =>
                        <View key={index} style={styles.expenseDetail}>
                            <Text style={styles.expenseLabel}>{item.label}</Text>
                            <Text style={styles.expenseValue}>{item.amount?.toFixed(2)} €</Text>
                        </View>
                    )}
                    <Legend data={chartData2} />
                    {Platform.OS !== "web" ?
                        <View style={styles.chart}>
                            <Victory.PolarChart
                                data={chartData2}
                                colorKey="color"
                                labelKey="label"
                                valueKey="amount"
                            >
                                <Victory.Pie.Chart
                                    innerRadius={60}
                                />
                            </Victory.PolarChart>
                        </View>
                        :
                        <Text>Statistiques temporairement indisponibles</Text>
                    }
                </View>
            )
            }
            <View style={styles.tabContent}>
                <Text style={styles.netIncomeLabel}>Revenus Net</Text>
                <Text style={styles.netIncomeValue}>{netIncome?.toFixed(2)} €</Text>
            </View>
            <Legend data={chartData} />
            <View style={styles.chart}>
                {Platform.OS !== "web" ?
                    <Victory.PolarChart
                        data={chartData}
                        colorKey="color"
                        labelKey="label"
                        valueKey="amount"
                    >
                        <Victory.Pie.Chart
                            innerRadius={60}
                        />
                    </Victory.PolarChart>
                    :
                    <Text>Statistiques temporairement indisponibles</Text>
                }
            </View>
        </View >
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        flex: 1,
        gap: 20,
    },
    heading: {
        fontSize: 20,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    tabs: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 20,
    },
    tabButton: {
        padding: 10,
        borderWidth: 1,
        borderColor: '#C4027A',
        borderRadius: 20,
        marginHorizontal: 5,
    },
    activeTabButton: {
        backgroundColor: '#C4027A',
    },
    tabText: {
        color: '#C4027A',
        fontWeight: 'bold',
    },
    activeTabText: {
        color: '#FFFFFF',
        fontWeight: 'bold',
    },
    tabContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginVertical: 10,
    },
    label: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    value: {
        fontSize: 18,
    },
    netIncomeLabel: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    netIncomeValue: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        color: '#C4027A',
    },
    chart: {
        height: 200,
        marginTop: 20,
    },
    expenseDetail: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 5,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    expenseLabel: {
        fontSize: 16,
    },
    expenseValue: {
        fontSize: 16,
    },
    chevron: {
        marginRight: 10,
    },
});

export default Details;
